import { FC } from "react";
import { HomepageHero } from "../../organisms/HomepageHero";
import { Page } from "../../templates/Page";
import { DesktopSpacer } from "../../templates/Spacer";
import { H2 } from "../../atoms/Heading";
import { Link } from "@components/atoms";
import { P1 } from "../../atoms/Paragraph";
import { ProductCard } from "components/molecules/ProductCard";
import { BetterButton } from "components/atoms/BetterButton";
import { NewZigZag } from "components/molecules/NewZigZag";
import { VideoPlayerSection } from "components/molecules/VideoPlayerSection";
import { CallbackAppOnly } from "components/molecules/CallbackAppOnly";
import { QuoteCarousel } from "components/organisms/QuoteCarousel";
import BottomSection from "components/organisms/BottomSection";
import { KeyResourcesItemProps } from "components/atoms/KeyResourcesItem";
import { color } from "@styles";
import { useLottie } from "hooks/useLottie";
import { useTranslations } from "next-intl";
import data from "./translations";
import { useLocale } from "hooks";
import { StaticRoutes } from "@routes";
import { useLocaleData } from "hooks/useLocaleData";
import {
  ButtonWrapper,
  CaseStudiesFlex,
  FlexCenter,
  LottieFile,
  ProductCardsSection,
  ProductOverviewSection,
  ProductOverviewSectionCopy,
  StyledImg,
} from "./styles";
import { clouds } from "./Clouds";
import { VideoPlayer } from "components/atoms/VideoPlayer";
import { LatestCaseStudy } from "utils/server";
import { ResponsiveZigZagImage } from "components/atoms/ResponsiveZigZagImage";
import { getProductCardDimensions, ProductCardDimensions } from "@utils";
import { StandardSection } from "components/templates/StandardSection";
import { NewProductIntro } from "components/atoms/NewProductIntro";
import { AdviserResourceCard } from "components/molecules/AdviserResourceCard";

interface HomePageProps {
  keyResources: KeyResourcesItemProps[];
  latestCaseStudy: LatestCaseStudy;
}

const Index: FC<HomePageProps> = ({ keyResources, latestCaseStudy }) => {
  const t = useTranslations("home");
  const { locale } = useLocale();
  const { products, zigzag, productSubheader, caseStudyData } = useLocaleData(data);
  const zigZagAnimationRef = useLottie(
    "/static/lottie/YugiEarth.json",
    undefined,
    "An animated illustration of Yugi the giraffe, the YuLife mascot, embracing the world representing YuLife’s ESG support.",
  );

  const PRODUCT_CARD_DIMENSIONS: ProductCardDimensions = {
    us: {
      height: "25.5rem",
      width: "16rem",
      justifyContent: "flex-start",
    },
    za: {
      height: "18.5rem",
      width: "22rem",
      justifyContent: "flex-start",
    },
    uk: {
      height: "29.625rem",
      width: "16rem",
      justifyContent: "space-around",
    },
  };

  const productCardPadding = locale !== "us" ? true : false;

  const callToAction = locale === "uk" ? "Get a quote" : "Speak to our team";

  return (
    <Page
      showAnnouncementBar
      latestCaseStudy={latestCaseStudy}
      navColor="transparent"
      hero={
        <>
          <HomepageHero
            title={t<string>("title")}
            description={t<string>("subtitle")}
            callToAction={callToAction}
            clouds={clouds}
            backgroundColor="#fefbe7"
            statBar={{
              heading: "#1 rated employee benefit",
              backgroundColor: color.S3100,
              tooltipCopy: t<string>("stats.tooltip"),
              stats: [
                {
                  value: "93%",
                  description: "highly engaged users",
                  tooltip:
                    "People who have downloaded the app in the last year and have been active in the last 30 days",
                },
                {
                  value: "181%",
                  description: "return on investment",
                  tooltip: t<string>("stats.stat-2.tooltip"),
                },
                {
                  value: "85%",
                  description: "feel more productive",
                  tooltip: "YuLife user survey, 2022",
                },
              ],
            }}
          />
        </>
      }
      fullScreenHero
    >
      <ProductOverviewSectionCopy>
        <H2
          align="center"
          pt="7rem"
          fontSize="3rem"
          style={{ maxWidth: "58rem", margin: "0 auto" }}
        >
          {t<string>("insurance-header")}
        </H2>
        <P1
          align="center"
          fontSize="1.25rem"
          lineHeight="2rem"
          style={{ maxWidth: "58rem", margin: "0 auto" }}
        >
          {productSubheader?.subheader || null}
        </P1>
      </ProductOverviewSectionCopy>
      <ProductOverviewSection>
        <ProductCardsSection>
          {products
            .filter((item) => item.isLive === undefined || item.isLive === true)
            .map((product) => (
              <ProductCard
                key={product.name}
                title={product.name}
                iconName={product.iconName}
                description={product.description}
                href={product.href}
                justifyContent={getProductCardDimensions(
                  "justifyContent",
                  locale,
                  PRODUCT_CARD_DIMENSIONS,
                )}
                width={getProductCardDimensions("width", locale, PRODUCT_CARD_DIMENSIONS)}
                height={getProductCardDimensions("height", locale, PRODUCT_CARD_DIMENSIONS)}
                hasPadding={productCardPadding}
              />
            ))}
        </ProductCardsSection>
        {locale !== "us" && (
          <ButtonWrapper>
            <Link href="/get-a-quote/" passHref legacyBehavior>
              <BetterButton text={callToAction} width="16rem" />
            </Link>
          </ButtonWrapper>
        )}
      </ProductOverviewSection>
      <DesktopSpacer height="4.7rem" />
      <NewZigZag
        title="Healthy business starts with a healthy life"
        description={[t("zigzag-healthy-business.description")]}
        align="left"
        reverse
        linkUrl={StaticRoutes.featuresEmployeeApp}
        linkText="Explore the app"
        paragraphpb="1.5rem"
      >
        <FlexCenter>
          <StyledImg
            locale={locale}
            src={t<string>("zigzag-healthy-business.image")}
            alt={t<string>("zigzag-healthy-business.alt")}
            width={locale === "us" ? 544 : 336}
            height={locale === "us" ? 544 : 537}
          />
        </FlexCenter>
      </NewZigZag>
      <NewZigZag
        title="Wellbeing that works for everyone"
        description={zigzag?.description || ""}
        align="left"
        linkUrl={StaticRoutes.featuresWellbeingHub}
        linkText="Learn more"
        paragraphpb="1.5rem"
        pb="7.5rem"
      >
        {locale === "uk" ? (
          <VideoPlayer webm={t<string>("zigzag-wellbeing-that-works.image")} />
        ) : (
          <ResponsiveZigZagImage
            src={t<string>("zigzag-wellbeing-that-works.image")}
            alt={t<string>("zigzag-wellbeing-that-works.alt")}
          />
        )}
      </NewZigZag>
      <NewZigZag
        title="Turn healthy habits into a force for good"
        description={zigzag?.description2}
        align="left"
        reverse
        linkUrl={StaticRoutes.featuresSocialImpactBenefits}
        linkText="Find out more"
        paragraphpb="1.5rem"
        pt="0rem"
      >
        <FlexCenter>
          <LottieFile ref={zigZagAnimationRef} />
        </FlexCenter>
      </NewZigZag>
      {locale === "us" && (
        <ButtonWrapper>
          <Link href="/get-a-quote/" passHref legacyBehavior>
            <BetterButton text="Get started with YuLife" width="20rem" />
          </Link>
        </ButtonWrapper>
      )}
      {locale !== "us" ? (
        <VideoPlayerSection
          title="It’s more than just insurance"
          videoImg="/static/img/VideoPlayer/VideoPlayerImg.jpg"
          altText="A still from a promotional YuLife video"
          vimeoId={t<string>("vimeo-id")}
        />
      ) : (
        <StandardSection background={color.S310} pb="0">
          <NewProductIntro
            title="The power of partnership"
            description="Whether it’s improving engagement, getting more value for money or improving total wellbeing - read how businesses are using YuLife to create happier and healthier employees."
            pt="0"
            pb="5rem"
            backgroundColor={color.S310}
          />
          <CaseStudiesFlex>
            {caseStudyData.map((card, index) => (
              <AdviserResourceCard
                key={card.title + index}
                imgSrc={card.imgSrc}
                imgAlt={card.imgAlt}
                title={card.title}
                resourceType={card.resourceType}
                description={card.description}
                slug={card.slug}
                linkText={card.linkText}
              />
            ))}
          </CaseStudiesFlex>
          <ButtonWrapper>
            <Link href="/case-studies//" passHref legacyBehavior>
              <BetterButton text="More case studies" />
            </Link>
          </ButtonWrapper>
        </StandardSection>
      )}
      {locale !== "uk" ? null : (
        <CallbackAppOnly subtitle={t<string>("callback-app-only.subtitle")} />
      )}
      <QuoteCarousel
        slides={[
          {
            quote:
              "To have a happy and healthy business I think you need happy and healthy people, and YuLife has really helped us do that.",
            source: "Clare Jones, Head of HR at Fladgate",
            avatar: "/static/img/carousel/clare-fladgate.png",
            vimeoId: "362316499",
          },
          {
            quote:
              "I am in no doubt that YuLife has made a positive impact on employees’ health and wellbeing – and will continue to do so for years to come.",
            source: "Alix Hobbs, Director of Operations at Cloud International",
            avatar: "/static/img/carousel/alix-cloud.png",
            vimeoId: "769492152",
          },
          {
            quote:
              "I am very proud of the wellness programme at Cloud International. Before YuLife, it didn’t exist… it now forms a fundamental part of our culture.",
            source: "Alix Hobbs, Director of Operations at Cloud International",
            avatar: "/static/img/carousel/alix-cloud.png",
            vimeoId: "769492152",
          },
          {
            quote:
              "It allows employees to combine life insurance with wellbeing benefits – it’s great for team engagement.",
            source: "Satvir & Lorraine, Ellsar Ltd Partner Agents at Vodafone",
            caseStudyURL:
              "https://yulife.com/case-studies/yulife-inspires-healthy-performance-among-employees-at-vodafone-franchise-stores/",
          },
          {
            quote:
              "YuLife has not only helped employee engagement but has enabled the virtual team to feel part of the wider community.",
            source: "Allison Cummins, Head of People and Capability at Apexon",
          },
        ]}
      />
      <BottomSection
        keyResources={locale === "uk" ? keyResources || [] : null}
        requestCallbackProps={{
          title: t<string>("request-callback-title"),
          subtitle: t<string>("request-callback-subtitle"),
          buttonText: callToAction,
        }}
      />
    </Page>
  );
};

export default Index;
